/**
 * PATH: packages/email-hub/src/feature/email-layout/MobileFolderSheet.tsx
 * FILE_ID: EMAIL-LAYOUT-MOBILE-FOLDER-SHEET-001
 * PURPOSE: Replace the mobile horizontal folder scroller with a stable sheet.
 * WHY: Folder navigation must remain open while its content scrolls.
 */

import { Check, ChevronDown, Inbox, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AdaptiveOverlay } from '@quotech/ui'

import { cn } from '../../_internal/lib/utils'
import { EMAIL_NAV_ITEMS } from '../config/navigation'
import { emailPath } from '../lib/basePath'
import { AddMailboxNavRow } from './AddMailboxNavRow'
import type { EmailLayoutController } from './useEmailLayoutController'

function pathFor(item: (typeof EMAIL_NAV_ITEMS)[number]) {
  return emailPath(item.href.replace(/^\/email/, ''))
}

export function MobileFolderSheet({
  layout,
}: {
  layout: EmailLayoutController
}) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const current = useMemo(
    () =>
      EMAIL_NAV_ITEMS.find((item) => {
        const target = pathFor(item)
        return location.pathname === target
          || location.pathname.startsWith(`${target}/`)
      }) ?? EMAIL_NAV_ITEMS[0],
    [location.pathname],
  )
  const CurrentIcon = current.icon

  if (!layout.showMobileFolderStrip) return null

  return (
    <>
      <div className="flex shrink-0 items-center gap-1.5 border-b border-border bg-muted/20 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs font-medium"
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid="email-mobile-folder-button"
        >
          <CurrentIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">
            {current.label}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        {layout.emailAccounts.length > 0 && (
          <button
            ref={layout.mailboxesBtnRef}
            type="button"
            onClick={() => {
              setOpen(false)
              layout.setMailboxesDockOpen(true)
            }}
            className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-border px-2 text-xs text-muted-foreground"
            title="Connected mailboxes"
          >
            <Inbox className="h-3.5 w-3.5" />
            <span>{layout.emailAccounts.length}</span>
          </button>
        )}
      </div>

      <AdaptiveOverlay
        open={open}
        onOpenChange={setOpen}
        mode="sheet"
        label="Choose email folder"
        testId="email-mobile-folder-sheet"
      >
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-muted-foreground/25" />
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold">Email folders</h2>
                  <p className="text-[10px] text-muted-foreground">
                    Choose once; the sheet closes after navigation
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-accent"
                  aria-label="Close folder sheet"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-1.5">
                {EMAIL_NAV_ITEMS.map((item) => {
                  const Icon = item.icon
                  const selected = item.id === current.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setOpen(false)
                        navigate(pathFor(item))
                      }}
                      className={cn(
                        'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs',
                        selected
                          ? 'border-primary/40 bg-primary/10 text-primary'
                          : 'border-border hover:bg-accent',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        {item.label}
                      </span>
                      {selected && <Check className="h-3.5 w-3.5" />}
                    </button>
                  )
                })}
              </div>

              <div className="mt-3 border-t border-border pt-3">
                <AddMailboxNavRow
                  variant="pill"
                  open={layout.addMailboxPickerOpen}
                  setOpen={layout.setAddMailboxPickerOpen}
                  onMicrosoft={() => {
                    setOpen(false)
                    void layout.connectOutlook()
                  }}
                  onGoogle={() => {
                    setOpen(false)
                    void layout.gmail.authorize()
                  }}
                />
              </div>
      </AdaptiveOverlay>
    </>
  )
}
